import { useMemo, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'shared/components/image';
import { MarkdownView } from 'shared/components/markdown-view';
import { PageContent } from 'shared/components/page-content';
import type { compileMarkdown } from 'shared/library/compile-markdown.server';

export type BreedAdvisorGuideProps = { compiledTree: ReturnType<typeof compileMarkdown> };
type MarkdownNode = BreedAdvisorGuideProps['compiledTree']['children'][number];
type MarkdownNodeProps = { node?: MarkdownNode };

const photoRatios: Record<string, number> = {
  '/images/breed-advisor/guide/golden-retriever.webp': 1000 / 667,
  '/images/breed-advisor/guide/whippet.webp': 1200 / 904,
  '/images/breed-advisor/guide/pudel-mellan.webp': 1000 / 796,
};

const createGuideComponents = (tableLabel: string) => ({
  img: ({ src, alt, title }: ComponentProps<'img'> & MarkdownNodeProps) => {
    if (typeof src !== 'string') return null;

    return (
      <figure className="not-prose my-8">
        <div className="relative" style={{ aspectRatio: photoRatios[src] ?? 3 / 2 }}>
          <Image
            src={src}
            alt={alt}
            sizeType="large"
            containerClassName="absolute inset-0"
            imageClassName="rounded object-cover"
            sizes="(max-width: 767px) calc(100vw - 24px), (max-width: 1023px) calc(100vw - 48px), 768px"
          />
        </div>
        {title && (
          <figcaption className="text-brand-brown-dark/70 mt-2 text-sm leading-relaxed">
            {title}
          </figcaption>
        )}
      </figure>
    );
  },
  table: ({ node: _node, ...props }: ComponentProps<'table'> & MarkdownNodeProps) => (
    <div className="my-6 overflow-x-auto" role="region" aria-label={tableLabel} tabIndex={0}>
      <table {...props} className="my-0 min-w-full" />
    </div>
  ),
});

export function BreedAdvisorGuide({ compiledTree }: BreedAdvisorGuideProps) {
  const { t } = useTranslation('components');
  const guideComponents = useMemo(
    () => createGuideComponents(t('breed_advisor.guide.table_label')),
    [t],
  );

  return (
    <section
      id="hundrasguiden"
      aria-label={t('breed_advisor.guide.label')}
      className="scroll-mt-20"
    >
      <PageContent className="!pt-0">
        <MarkdownView
          markdown=""
          compiledTree={compiledTree}
          customComponents={guideComponents}
          className="mt-6 md:mt-12"
        />
      </PageContent>
    </section>
  );
}
